import { useQueryClient } from "@tanstack/react-query";
import { t } from "ttag";

import { appQueriesStore } from "@/app/api";
import { useModalsDisplayStore } from "@/app/stores";
import { consultationsQueriesStore } from "@/features/consultations/api";
import { useScheduleContext } from "@/features/consultations/contexts";
import {
  useRescheduleAppointment,
  useScheduleAppointment,
} from "@/features/consultations/hooks";
import { APPOINTMENT_TYPE } from "@/features/consultations/models";
import type { Appointment } from "@/features/consultations/models";
import {
  ScheduleErrorModal,
  ScheduleSuccessModal,
} from "@/features/consultations/sections/schedule";
import { LogoLoader } from "@/shared/components";
import { useBoolean } from "@/shared/hooks";
import { ERROR_CODES } from "@/shared/models";
import { Button, validateError } from "@/shared/ui";

export const ScheduleSubmit = () => {
  const markJourneySupportPromotionAsSeen = useModalsDisplayStore(
    (state) => state.markJourneySupportPromotionAsSeen,
  );

  const queryClient = useQueryClient();

  const {
    value: showErrorModal,
    setFalse: closeErrorModal,
    setTrue: openErrorModal,
  } = useBoolean(false);
  const { value: showSuccessModal, setTrue: openSuccessModal } =
    useBoolean(false);

  const {
    language,
    scheduleDate: { timeSlot },
    appointmentType: type,
    actions: { setProvider },
    appointmentToReschedule,
    subscriptionId,
  } = useScheduleContext();

  const { mutation: scheduleAppointment } = useScheduleAppointment();
  const rescheduleAppointment = useRescheduleAppointment();

  const handleSubmit = () => {
    if (timeSlot) {
      const mutateOptions = {
        onError: (error: unknown) => {
          const validatedError = validateError(error);

          if (
            validatedError?.response.data.error.code ===
            ERROR_CODES.UNAVAILABLE_TIME_SLOT
          ) {
            return void queryClient.invalidateQueries({
              queryKey:
                consultationsQueriesStore.consultations.providers._ctx
                  .availability.queryKey,
            });
          }

          openErrorModal();
        },
        onSuccess: (appointment: { provider: Appointment["provider"] }) => {
          if (appointment.provider) {
            setProvider(appointment.provider);
          }
          openSuccessModal();

          if (type === APPOINTMENT_TYPE.JOURNEY_SUPPORT_INITIAL) {
            markJourneySupportPromotionAsSeen({ stopShowing: true });
          }
        },
      };

      if (appointmentToReschedule) {
        rescheduleAppointment.mutate(
          {
            timeSlot,
            id: appointmentToReschedule.id,
            subscriptionId,
            appointmentType: type,
          },
          mutateOptions,
        );
      } else {
        scheduleAppointment.mutate(
          { timeSlot, language, type, subscriptionId },
          mutateOptions,
        );
      }
    }
  };

  const isPending =
    scheduleAppointment.isPending || rescheduleAppointment.isPending;

  const handleSuccessModalClose = () => {
    void queryClient.invalidateQueries({
      queryKey: appQueriesStore.user.queryKey,
    });
  };

  return (
    <>
      <Button
        disabled={!timeSlot || isPending}
        size="lg"
        className="w-full"
        onClick={handleSubmit}
      >
        {t`Confirm Consultation`}
      </Button>

      <LogoLoader show={isPending} fullscreen />

      <ScheduleErrorModal show={showErrorModal} onClose={closeErrorModal} />

      <ScheduleSuccessModal
        show={showSuccessModal}
        onClose={handleSuccessModalClose}
      />
    </>
  );
};
